// Lib
import { createSelector } from 'reselect';
import getUserLocale from 'get-user-locale';

// Utils
import { ImMap, propIn } from '../../common/utils/immutableHelper';
import { createShallowSelector } from '../utils/milanoteReselect/milanoteReselect';
import { getUsers } from './usersSelector';
import { isStaff, isUserSubscriptionAdmin } from './userUtil';
import {
    getUserCurrencyPreference,
    getUserEnableDoubleClickToCreateNotes,
    getUserEnableDrawingInkEffect,
    getUserEnableUndoRedoButtonsForIpad,
    getUserEnableUndoRedoButtonsForWeb,
    getUserLanguagePreference,
    getUserLocalePreference,
} from '../../common/users/utils/userPropertyUtils';
import { isPlatformIpad } from '../platform/utils/platformDetailsUtils';
import { getPlatformDetailsSelector } from '../platform/platformSelector';

// Types
import { MNUser } from '../../common/users/userModelTypes';
import { MNFullSubscriptionDetail } from '../../common/payment/subscriptions/subscriptionTypes';

// TODO: Move this to currentUserReducer once it has been converted to TypeScript
export type MNCurrentUser = MNUser & {
    _id: string;
    viewedBoards: { [key: string]: string };
    isGuest: boolean;
    authenticated: boolean;
    fetched: boolean;
};

// TODO: Move this to currentUserReducer once it has been converted to TypeScript
export type ImMNCurrentUser = ImMap<MNCurrentUser>;

export const getCurrentUser = (state: any): ImMNCurrentUser => propIn(['app', 'currentUser'])(state);
export const getCurrentUserId = (state: any): string => state.getIn(['app', 'currentUser', '_id']);
export const getCurrentUserClientId = (state: any): string => state.getIn(['app', 'currentUser', 'clientId']);
export const getCurrentUserEmail = (state: any): string => state.getIn(['app', 'currentUser', 'email']);
export const getIsCurrentUserEmailVerified = (state: any): boolean =>
    !!state.getIn(['app', 'currentUser', 'emailVerifiedDate']);
export const getCurrentUserRootBoardId = (state: any): string => state.getIn(['app', 'currentUser', 'rootBoardId']);
export const isCurrentUserFetched = (state: any): boolean => state.getIn(['app', 'currentUser', 'fetched']);
export const getCurrentUserTemplateRootBoardId = (state: any): string =>
    state.getIn(['app', 'currentUser', 'templateRootId']);
export const getCurrentUserQuickNotesRootBoardId = (state: any): string =>
    state.getIn(['app', 'currentUser', 'quickNotesRootId']);
export const isGuestSelector = (state: any): boolean => state.getIn(['app', 'currentUser', 'isGuest']);
export const getLastCheckedQuickNotes = (state: any): number =>
    state.getIn(['app', 'currentUser', 'lastCheckedQuickNotes']);
export const getCurrentUserExperiments = (state: any) => state.getIn(['app', 'currentUser', 'experiments']);

export const getCurrentUserSegment = (state: any): string | undefined => {
    const segment = state.getIn(['app', 'currentUser', 'userSegment', 'segment']);
    if (segment) return segment;

    // Fallback for old user data
    const legacySegments = state.getIn(['app', 'currentUser', 'segments']);
    if (legacySegments && legacySegments.size > 0) {
        return legacySegments.first();
    }
};

export const getCurrentUserLastViewedBoardId = (state: any) => state.getIn(['app', 'currentUser', 'lastViewedBoard']);
export const getCurrentUserEducation = (state: any) => state.getIn(['app', 'currentUser', 'education']);
export const getCurrentUserAccountCreationDate = (state: any) =>
    state.getIn(['app', 'currentUser', 'accountCreationDate']);
export const getCurrentUserOTToken = (state: any) => state.getIn(['app', 'currentUser', 'otToken']);
export const getCurrentUserAccountRegistrationDate = propIn(['app', 'currentUser', 'accountRegistrationDate']);
export const getCurrentUserInitialisationLastViewedBoardId = (state: any) =>
    state.getIn(['app', 'currentUser', 'initialisationLastViewedBoardId']);
export const isCurrentUserSettingDrawingInkEffectEnabled = (state: any) =>
    getUserEnableDrawingInkEffect(state.getIn(['app', 'currentUser']));

export const getCurrentUserRole = propIn(['app', 'currentUser', 'role']);
export const isCurrentUserStaff = (state: any) => isStaff(getCurrentUserRole(state));

export const isUserSubscribed = (state: any) => state.getIn(['app', 'currentUser', 'subscription', 'active']);
export const getCurrentUserSubscriptionId = (state: any) => state.getIn(['app', 'currentUser', 'subscriptionId']);
export const getCurrentUserSubscription = (state: any): ImMap<MNFullSubscriptionDetail> =>
    state.getIn(['app', 'currentUser', 'subscription']);
export const getCurrentUserSubscriptionPlan = (state: any) =>
    state.getIn(['app', 'currentUser', 'subscription', 'plan']);
export const getCurrentUserCustomer = (state: any) => state.getIn(['app', 'currentUser', 'customer']);
export const getCurrentUserCustomerId = (state: any) => state.getIn(['app', 'currentUser', 'customerId']);

export const getCurrentUserUsedPlatforms = (state: any) => state.getIn(['app', 'currentUser', 'usedPlatforms']);

export const getIsDoubleClickToCreateNotesSettingEnabled = createSelector(getCurrentUser, (currentUser) =>
    getUserEnableDoubleClickToCreateNotes(currentUser),
);

//  This is a special case for the iPad app, where we the default setting is true for the undo/redo buttons
export const getIsUndoRedoButtonsSettingEnabled = createSelector(
    getCurrentUser,
    getPlatformDetailsSelector,
    (currentUser, platformDetails) => {
        if (isPlatformIpad(platformDetails)) {
            return getUserEnableUndoRedoButtonsForIpad(currentUser);
        }
        return getUserEnableUndoRedoButtonsForWeb(currentUser);
    },
);

export const currentUserIsSubscriptionAdminSelector = createSelector(
    getCurrentUser,
    getCurrentUserSubscription,
    (currentUser, subscription) => isUserSubscriptionAdmin(currentUser, subscription),
);

export const subscriptionTeamSelector = createShallowSelector(
    getCurrentUserSubscription,
    getUsers,
    (subscription, users) => {
        const memberIds = subscription && subscription.get('teamMemberIds');
        if (!memberIds) return null;

        return memberIds.map((userId) => users.get(userId)).filter((user) => user);
    },
);

export const subscriptionAdminUserSelector = createSelector(
    getCurrentUserSubscription,
    getUsers,
    (subscription, users) => {
        if (!users || !subscription) return;

        const adminId = subscription.get('adminId');
        return users.get(adminId);
    },
);

export const isUserAvailableSelector = createSelector(
    getCurrentUser,
    isGuestSelector,
    (currentUser, isGuest) => isGuest || (currentUser.get('authenticated') && currentUser.get('fetched')) || false,
);

export const userLanguagePreferenceSelector = createSelector(
    getCurrentUser,
    (currentUser) => getUserLanguagePreference(currentUser) || getUserLocale() || 'en-US',
);

export const userCurrencyPreferenceSelector = createSelector(
    getCurrentUser,
    (currentUser) => getUserCurrencyPreference(currentUser) || 'USD'
);

export const userLocalePreferenceSelector = createSelector(
    getCurrentUser,
    (currentUser) => getUserLocalePreference(currentUser) || 'en',
);
